import type { Idioma } from '../../i18n/index.js';
import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';

/**
 * CUENTAS-01 (decisión de Iván, 2026-09-27, cambia el principio 9 de `plan/PLAN.md` § 1: Athena ya
 * no es "sin cuenta"). Registro y login por enlace mágico, sin contraseña.
 *
 * **Este cliente sí persiste sesión** (a diferencia del `crearClienteAnon` del backend): es el
 * único sitio de la web que habla con Supabase Auth directamente. El resto de la app (leer el
 * perfil, guardar un consentimiento) pasa siempre por el API con el JWT en `Authorization`, nunca
 * llama a Supabase a pelo: es más simple razonar sobre una sola puerta hacia el backend.
 *
 * **El cliente se crea de forma perezosa**, no al importar este módulo. Sin `VITE_SUPABASE_URL`/
 * `VITE_SUPABASE_ANON_KEY` en el build (pasó en producción justo tras desplegar CUENTAS-01b: el
 * sitio estático de Render no tenía esas dos variables), `createClient(undefined, undefined)` lanza
 * `supabaseUrl is required` **al construirse**, y como este módulo lo importa `useSesion`, que
 * `Marco` monta siempre, la web entera se quedaba en blanco. Con la construcción diferida a la
 * primera llamada real, y `AUTH_DISPONIBLE` para que el resto de la app pueda ocultar el login en
 * vez de intentarlo, faltar esas variables ya no puede tumbar nada más que el login mismo.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const CLAVE = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** `true` si las dos variables de build están puestas: solo entonces existe el cliente de verdad. */
export const AUTH_DISPONIBLE = Boolean(SUPABASE_URL) && Boolean(CLAVE);

let cliente: SupabaseClient | null = null;

function clienteDeAuth(): SupabaseClient {
  if (cliente === null) {
    if (!AUTH_DISPONIBLE) throw new Error('Supabase Auth no está configurado: faltan VITE_SUPABASE_URL o VITE_SUPABASE_ANON_KEY.');
    cliente = createClient(SUPABASE_URL, CLAVE, { auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true } });
  }
  return cliente;
}

/**
 * Pide el enlace mágico. Solo el registro permite crear una cuenta nueva. `null` si Supabase lo aceptó; el mensaje de error si no (email inválido,
 * etc.). CAPTCHA-01: `captchaToken`, si se pasa, va como `options.captchaToken` (soporte nativo del
 * SDK); Supabase decide si hace falta usarlo, según lo que tenga activado en su dashboard.
 */
export async function pedirEnlaceMagico(
  email: string,
  captchaToken?: string,
  volverA?: string,
  crearCuenta = false,
  idioma: Idioma = 'es',
): Promise<string | null> {
  const volver = rutaPropia(volverA) ?? '/mi-cuenta';
  const retorno = new URL('/auth/retorno', window.location.origin);
  retorno.searchParams.set('idioma', idioma);
  retorno.searchParams.set('volver', volver);
  const { error } = await clienteDeAuth().auth.signInWithOtp({
    email,
    options: {
      shouldCreateUser: crearCuenta,
      ...(captchaToken === undefined ? {} : { captchaToken }),
      // MAIL-I18N: RedirectTo llega a la plantilla también en cuentas existentes.
      // El callback conserva idioma y retorno OAuth/hilo tras inicializar la sesión.
      emailRedirectTo: retorno.href,
    },
  });
  return error?.code ?? error?.message ?? null;
}

/** Valida el código recibido para este correo; Supabase persiste y notifica la nueva sesión. */
export async function verificarCodigo(email: string, token: string): Promise<string | null> {
  const { data, error } = await clienteDeAuth().auth.verifyOtp({ email, token, type: 'email' });
  if (error !== null) return error.code ?? error.message;
  return data.session === null ? 'no_session' : null;
}

/**
 * Una ruta de esta misma web a la que volver, o `null`. Solo rutas propias (`/algo`): nada de
 * `//otro-dominio` ni direcciones completas, que convertirían el enlace mágico en una redirección
 * abierta hacia cualquier sitio.
 */
export function rutaPropia(ruta: string | null | undefined): string | null {
  if (ruta === null || ruta === undefined) return null;
  return /^\/(?![/\\])\S*$/u.test(ruta) ? ruta : null;
}

/**
 * MCP-01, pantalla de consentimiento del OAuth Server de Supabase (`/oauth/consent`). Llamadas
 * finas sobre `auth.oauth` de supabase-js; la redirección la hace la página (`skipBrowserRedirect`),
 * para poder probarla y para que la usuaria vea el resultado antes de salir.
 */
export type DetallesDeAutorizacion =
  | {
      readonly tipo: 'pedir';
      readonly cliente: { readonly nombre: string; readonly web: string };
      readonly vuelveA: string;
      readonly permisos: string;
      readonly correo: string;
    }
  | { readonly tipo: 'yaConcedida'; readonly irA: string };

export async function detallesDeAutorizacion(id: string): Promise<DetallesDeAutorizacion | null> {
  if (!AUTH_DISPONIBLE) return null;
  const { data, error } = await clienteDeAuth().auth.oauth.getAuthorizationDetails(id);
  if (error !== null) return null;
  if ('authorization_id' in data) {
    return {
      tipo: 'pedir',
      cliente: { nombre: data.client.name, web: data.client.uri },
      vuelveA: data.redirect_uri,
      permisos: data.scope,
      correo: data.user.email,
    };
  }
  return { tipo: 'yaConcedida', irA: data.redirect_url };
}

/** Permite o deniega; devuelve la dirección a la que volver (con el código o con el rechazo), o `null` si falla. */
export async function decidirAutorizacion(id: string, permitir: boolean): Promise<string | null> {
  if (!AUTH_DISPONIBLE) return null;
  const oauth = clienteDeAuth().auth.oauth;
  const { data, error } = permitir
    ? await oauth.approveAuthorization(id, { skipBrowserRedirect: true })
    : await oauth.denyAuthorization(id, { skipBrowserRedirect: true });
  return error === null ? data.redirect_url : null;
}

/** Sin auth disponible, siempre `null`: es como estar sin sesión, no un error para el resto de la app. */
export async function sesionActual(): Promise<Session | null> {
  if (!AUTH_DISPONIBLE) return null;
  const { data } = await clienteDeAuth().auth.getSession();
  return data.session;
}

export function alCambiarSesion(cb: (sesion: Session | null) => void): () => void {
  if (!AUTH_DISPONIBLE) return () => undefined;
  const { data } = clienteDeAuth().auth.onAuthStateChange((_evento, sesion) => cb(sesion));
  return () => data.subscription.unsubscribe();
}

export async function cerrarSesion(): Promise<void> {
  if (!AUTH_DISPONIBLE) return;
  await clienteDeAuth().auth.signOut();
}

/** El JWT de la sesión actual, para mandarlo al API en `Authorization`. `null` si no hay sesión. */
export async function jwtActual(): Promise<string | null> {
  const sesion = await sesionActual();
  return sesion?.access_token ?? null;
}
